'use client'

import Link from 'next/link'
import { useEffect, useRef } from 'react'
import { renderToStaticMarkup } from 'react-dom/server'

import QRCodeStyling from 'qr-code-styling'

import { cn } from '@/app/utils/style'
import KiteIcon from '@/public/images/icon.svg'

interface QRCodeProps {
  url: string
  ariaLabel?: string
  className?: string
  margin?: number
  size?: number
}

/**
 * QRCode component that generates a QR code for a given URL
 * Wraps the QR code in a Link for accessibility
 */
export function QRCode({
  url,
  ariaLabel = 'QR Code',
  className,
  margin = 10,
  size = 200,
}: QRCodeProps) {
  const qrRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!url || !qrRef.current) return

    while (qrRef.current.firstChild) {
      qrRef.current.removeChild(qrRef.current.firstChild)
    }

    const svgString = renderToStaticMarkup(<KiteIcon width={128} height={128} />)
    const logoDataUrl = `data:image/svg+xml;utf8,${encodeURIComponent(svgString)}`

    // Generate QR Code
    const qrCode = new QRCodeStyling({
      width: size,
      height: size,
      type: 'svg',
      data: url,
      margin,
      qrOptions: {
        typeNumber: 0,
        mode: 'Byte',
        errorCorrectionLevel: 'H',
      },
      image: logoDataUrl,
      imageOptions: {
        hideBackgroundDots: true,
        imageSize: 0.4,
        margin: 5,
        crossOrigin: 'anonymous',
      },
      dotsOptions: {
        color: '#000000',
        type: 'rounded',
      },
      backgroundOptions: {
        color: '#ffffff',
      },
      cornersSquareOptions: {
        color: '#000000',
        type: 'extra-rounded',
      },
      cornersDotOptions: {
        color: '#000000',
        type: 'dot',
      },
    })

    qrCode.append(qrRef.current)
  }, [margin, size, url])

  if (!url) return null

  return (
    <div className={cn('p-xl rounded-md border-2 border-blue-100', className)}>
      <Link
        className="flex-1"
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={ariaLabel}
      >
        <div
          key={`${url}-${margin}-${size}`}
          ref={qrRef}
          className="flex items-center justify-center"
        />
      </Link>
    </div>
  )
}
